import { getUserAppointmentCancelReasonOptions } from "@/constants/cancelReasons";
import type { CustomerAppointmentCancelReason } from "@/server-types";
import { useEffect, useState } from "react";
import { Plural, Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { useCalendarStore } from "@/pages/Dashboard/Calendar/store/useCalendarStore";
import {
  addToast,
  Button,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  useDisclosure,
} from "@heroui/react";
import { LimeInput } from "@/Components/NextBase/LimeInput";
import { LimeSelect } from "@/Components/NextBase/LimeSelect";
import { SquareStack } from "lucide-react";
import { PrescheduledSelectModal } from "./PrescheduledSelectModal";
import { LimeModal } from "@/Components/NextBase/LimeModal";
import { AppointmentRefundFields } from "./AppointmentRefundFields";
import {
  SubscriptionCancellationDecisions,
  subscriptionDecisionDefaults,
  toSubscriptionCancellationDecision,
} from "./SubscriptionCancellationDecisions";
import { api, queryClient } from "@/lib/api-client";
import { useClientFeature } from "@/hooks/useClientFeature";
import { useForm } from "@mantine/form";
import { LimeCheckbox } from "@/Components/NextBase/LimeCheckbox";
import { LimeCollapse } from "@/Components/NextBase/LimeCollapse";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import { toCents } from "@/utils";

export const RemoveCustomerFromAppointmentModal = ({
  appointmentId,
  userAppointmentId,
  isOpen,
  onClose,
  customerId,
  paymentIntentId,
  appointmentDate,
}: {
  appointmentId: number;
  userAppointmentId: number;
  isOpen: boolean;
  onClose: () => void;
  customerId?: number;
  paymentIntentId?: string | null;
  appointmentDate?: string;
}) => {
  const { hasCancelNotification } = useCalendarStore((store) => store);
  const cancelReasonOptions = getUserAppointmentCancelReasonOptions();

  const { isFeatureEnabled } = useClientFeature();

  const {
    data: payment,
    isPending: isLoadingPayment,
    processedErrorMessage: getPaymentErrorMessage,
  } = api.stripe.useGetPaymentForOverview(
    userAppointmentId,
    isOpen && isFeatureEnabled("payments") && paymentIntentId != null,
  );

  const { data: options } =
    api.customerAppointment.useGetCustomerAppointmentCancelPreflight({
      customerAppointmentId: userAppointmentId,
      enabled: isOpen && isFeatureEnabled("customerSubscriptionMakeupCredits"),
    });

  const {
    mutateAsync: deleteCustomerPrescheduled,
    isPending: isDeletingCustomerPrescheduled,
  } = api.customer.useDeleteCustomerPrescheduled();

  const {
    isOpen: isPrescheduledSelectModalOpen,
    onOpen: onOpenPrescheduledSelectModal,
    onClose: onClosePrescheduledSelectModal,
  } = useDisclosure();

  const [additionalAppointmentIds, setAdditionalAppointmentIds] = useState<
    number[]
  >([]);

  const form = useForm<{
    cancelReason: CustomerAppointmentCancelReason;
    sendCancelNotifications: boolean;
    customCancelReason: string;
    shouldRefundPayment: boolean;
    refundAmount: number;
    refundReason: "requested_by_customer" | "duplicate" | "fraudulent";
    excludeFromSubscriptionUsage: boolean;
    issueMakeupCredit: boolean;
    reissueCredit: boolean;
    extendCredit: boolean;
  }>({
    initialValues: {
      cancelReason: "NoReasonProvided",
      sendCancelNotifications: hasCancelNotification,
      customCancelReason: "",
      shouldRefundPayment: false,
      refundAmount: 0,
      refundReason: "requested_by_customer",
      excludeFromSubscriptionUsage: false,
      issueMakeupCredit: false,
      reissueCredit: true,
      extendCredit: false,
    },
    validate: {
      customCancelReason: (value, values) => {
        if (values.cancelReason === "Custom" && value.trim().length === 0) {
          return t`Vnesite razlog za odstranitev`;
        }

        return null;
      },
      refundAmount: (value, values) => {
        if (!values.shouldRefundPayment) {
          return null;
        }

        if (value < 0.01) {
          return t`Znesek mora biti večji od 0`;
        }

        if (value > (payment?.refundableAmountCents || 0) / 100) {
          return t`Znesek ne sme biti večji od zneska plačila`;
        }

        return null;
      },
    },
  });

  useEffect(() => {
    if (!options) {
      return;
    }

    form.setValues(subscriptionDecisionDefaults(options));
  }, [options]);

  useEffect(() => {
    if (!payment) {
      return;
    }

    form.setValues({
      refundAmount: payment.maxNoLossRefundAmountCents / 100,
    });
  }, [payment]);

  const handleConfirm = async (values: typeof form.values) => {
    if (!customerId) {
      return;
    }

    await deleteCustomerPrescheduled({
      customerId,
      body: {
        appointmentIds: [appointmentId, ...additionalAppointmentIds],
        userAppointmentId:
          additionalAppointmentIds.length > 0 ? undefined : userAppointmentId,
        sendCancelNotifications: values.sendCancelNotifications,
        cancelReason: values.cancelReason,
        customCancelReason: values.customCancelReason,
        subscriptionDecision: toSubscriptionCancellationDecision(
          options,
          values,
        ),
        paymentRefundData: values.shouldRefundPayment
          ? {
              amountCents: toCents(values.refundAmount),
              reason: values.refundReason,
            }
          : undefined,
      },
    });

    queryClient.invalidateQueries({
      queryKey: ["customer"],
    });

    addToast({
      color: "success",
      title: t`Stranka uspešno odstranjena`,
    });

    onClose();
  };

  return (
    <>
      <LimeModal
        isOpen={isOpen}
        onClose={onClose}
        placement="center"
        closeButton={false}
        size="lg"
      >
        <ModalContent>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();

              const isValid = form.validate();
              if (isValid) {
                handleConfirm(form.getValues());
              }
            }}
          >
            <ModalHeader>
              <p className="text-sm">
                <Trans>
                  Ali ste prepričani, da želite odstraniti stranko s termina?
                </Trans>
              </p>
            </ModalHeader>
            <ModalBody>
              <div className="flex flex-col items-start gap-3">
                <LimeSelect
                  label={t`Razlog za odstranitev`}
                  items={cancelReasonOptions.map((option) => ({
                    label: option.label,
                    key: option.value,
                  }))}
                  selectedKeys={[form.values.cancelReason]}
                  onSingleItemChange={(value) => {
                    form.setFieldValue(
                      "cancelReason",
                      value as CustomerAppointmentCancelReason,
                    );
                  }}
                />
                {hasCancelNotification && (
                  <div className="mt-2 flex flex-col">
                    <LimeCheckbox
                      {...form.getInputProps("sendCancelNotifications", {
                        type: "checkbox",
                      })}
                    >
                      <Trans>Pošlji obvestila?</Trans>
                    </LimeCheckbox>
                  </div>
                )}

                <LimeCollapse isOpen={form.values.cancelReason === "Custom"}>
                  <LimeInput
                    className="mt-2"
                    label={t`Vnesite razlog`}
                    {...form.getInputProps("customCancelReason")}
                  />
                </LimeCollapse>

                <div>
                  <Button
                    variant="flat"
                    color="primary"
                    onPress={() => {
                      onOpenPrescheduledSelectModal();
                    }}
                    startContent={<SquareStack size={16} />}
                  >
                    <Trans>
                      Izberite več terminov, iz katerih želite odstraniti
                      stranko
                    </Trans>
                  </Button>
                  {additionalAppointmentIds.length > 0 ? (
                    <p className="text-sm text-gray-500">
                      <Plural
                        value={additionalAppointmentIds.length}
                        one={"Izbran je še # dodaten termin"}
                        two={"Izbrana sta še # dodatna termina"}
                        other={"Izbranih je še # dodatnih terminov"}
                      />
                    </p>
                  ) : undefined}
                </div>

                <SubscriptionCancellationDecisions
                  options={options}
                  form={form}
                />

                <LimeAlert message={getPaymentErrorMessage} />

                <AppointmentRefundFields
                  payment={payment}
                  isLoadingPayment={isLoadingPayment}
                  userAppointmentId={userAppointmentId}
                  shouldRefundPaymentProps={form.getInputProps(
                    "shouldRefundPayment",
                    { type: "checkbox" },
                  )}
                  refundAmountProps={form.getInputProps("refundAmount")}
                  refundReasonProps={form.getInputProps("refundReason")}
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="bordered" onPress={onClose}>
                <Trans>Prekliči</Trans>
              </Button>
              <Button
                color="danger"
                type="submit"
                data-identifier="confirm-remove-customer"
                isLoading={isDeletingCustomerPrescheduled}
              >
                <Trans>Odstrani stranko</Trans>
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </LimeModal>

      <PrescheduledSelectModal
        isOpen={isPrescheduledSelectModalOpen}
        overrideCustomerId={customerId}
        excludeAppointmentIds={[appointmentId]}
        appointmentDate={appointmentDate}
        onClose={(appointmentIds) => {
          if (appointmentIds) {
            setAdditionalAppointmentIds(appointmentIds);
          }
          onClosePrescheduledSelectModal();
        }}
      />
    </>
  );
};

export default RemoveCustomerFromAppointmentModal;
